/**
 * 顶部子系统（type=SUBSYSTEM）与侧栏切分
 * 侧栏叶子来自权限菜单数据；按 SUBSYSTEM.parent_id 树切分。
 */
import {
  buildMenuTree,
  getAllMenusFromStorage,
  type MenuPermissionItem,
} from '@/utils/permission';

/** 五个顶部子系统 code（与库表一致） */
export const SUBSYSTEM_CODES = {
  member: 'subsystem:member',
  training: 'subsystem:training',
  safety: 'subsystem:safety',
  energy: 'subsystem:energy',
  project: 'subsystem:project',
} as const;

/**
 * 已全部入库并由权限体系控制；保留空集合与工具函数，避免菜单管理/授权树旧引用报错。
 * 勿再往此集合加入已启用的子系统。
 */
export const DEFERRED_SUBSYSTEM_CODES = new Set<string>();

export function isDeferredSubsystemCode(code?: string): boolean {
  return !!code && DEFERRED_SUBSYSTEM_CODES.has(code);
}

export type SubsystemCode =
  (typeof SUBSYSTEM_CODES)[keyof typeof SUBSYSTEM_CODES];

const PROJECT_PATH_PREFIXES = [
  '/project/',
  '/member/system',
  '/system/space', // 旧前缀兼容（会 redirect）
];

/** 当前路径是否属于「项目配置」（空间/场馆及共享设施主数据 + 权限） */
export function isPoolSystemMenuPath(pathname: string): boolean {
  if (PROJECT_PATH_PREFIXES.some((p) => pathname.startsWith(p))) {
    return true;
  }
  // 旧 /member/space/* 设施与主数据路径仍归项目配置（会 redirect）
  return pathname.startsWith('/member/space');
}

/** 根据路径推断当前子系统 code */
export function resolveSubsystemCodeByPath(pathname: string): SubsystemCode {
  if (isPoolSystemMenuPath(pathname)) {
    return SUBSYSTEM_CODES.project;
  }
  if (pathname.startsWith('/safety')) {
    return SUBSYSTEM_CODES.safety;
  }
  if (pathname.startsWith('/device')) {
    return SUBSYSTEM_CODES.energy;
  }
  if (pathname.startsWith('/training')) {
    return SUBSYSTEM_CODES.training;
  }
  return SUBSYSTEM_CODES.member;
}

function menuCodeOf(item: { access?: string; code?: string }): string {
  return String(item?.access || item?.code || '').trim();
}

/** 从侧栏树中取出指定子系统的子模块（不展示 SUBSYSTEM 自身） */
export function pickSubsystemChildrenMenus(
  menuData: any[],
  subsystemCode: SubsystemCode | string,
): any[] {
  if (!Array.isArray(menuData)) {
    return [];
  }
  const root = menuData.find((item) => menuCodeOf(item) === subsystemCode);
  if (root?.children?.length) {
    return root.children;
  }
  // 兼容：尚未按子系统组树时，退回旧逻辑
  if (subsystemCode === SUBSYSTEM_CODES.project) {
    return menuData.filter((item) => {
      const code = menuCodeOf(item);
      return (
        code === 'project:basic' ||
        code === 'project:facility' ||
        code === 'project:member' ||
        code === 'project:system' ||
        code === 'space' ||
        code === 'system'
      );
    });
  }
  if (subsystemCode === SUBSYSTEM_CODES.member) {
    return menuData.filter((item) => {
      const code = menuCodeOf(item);
      if (!code || code.startsWith('subsystem:')) {
        return false;
      }
      return !(
        code === 'project:basic' ||
        code === 'project:facility' ||
        code === 'project:member' ||
        code === 'project:system' ||
        code === 'space' ||
        code === 'system'
      );
    });
  }
  return [];
}

export function pickSystemSettingsMenus(menuData: any[]): any[] {
  return pickSubsystemChildrenMenus(menuData, SUBSYSTEM_CODES.project);
}

export function pickMemberMenus(menuData: any[]): any[] {
  return pickSubsystemChildrenMenus(menuData, SUBSYSTEM_CODES.member);
}

export const POOL_SYSTEM_DEFAULT_PATH = '/project/basic/spaceList';

/** 顶栏子系统展示元数据（有授权叶子或 SUBSYSTEM 节点时才展示） */
const SUBSYSTEM_META: Array<{
  code: SubsystemCode;
  name: string;
  defaultUrl: string;
  pathPrefixes: string[];
  codePrefixes: string[];
  sortOrder: number;
}> = [
  {
    code: SUBSYSTEM_CODES.member,
    name: '泳池会员',
    defaultUrl: '/member/home/overview',
    pathPrefixes: ['/member/'],
    codePrefixes: ['member:', 'subsystem:member'],
    sortOrder: 1,
  },
  {
    code: SUBSYSTEM_CODES.training,
    name: '泳池培训',
    defaultUrl: '/training/coach/styleProfile',
    pathPrefixes: ['/training/'],
    codePrefixes: ['training:', 'subsystem:training'],
    sortOrder: 2,
  },
  {
    code: SUBSYSTEM_CODES.safety,
    name: '泳池安全',
    defaultUrl: '/safety/cameraList',
    pathPrefixes: ['/safety/'],
    codePrefixes: ['safety:', 'subsystem:safety'],
    sortOrder: 3,
  },
  {
    code: SUBSYSTEM_CODES.energy,
    name: '泳池设备',
    defaultUrl: '/device/DataStatistics',
    pathPrefixes: ['/device/'],
    codePrefixes: ['energy:', 'subsystem:energy'],
    sortOrder: 4,
  },
  {
    code: SUBSYSTEM_CODES.project,
    name: '项目配置',
    defaultUrl: POOL_SYSTEM_DEFAULT_PATH,
    pathPrefixes: [
      '/project/',
      '/member/system',
      '/member/space',
      '/system/space',
    ],
    codePrefixes: ['project:', 'subsystem:project'],
    sortOrder: 5,
  },
];

/** 菜单是否启用（兼容 status 缺省 / 字符串 "1"） */
export function isPermissionItemEnabled(item?: {
  status?: number | string | null;
}): boolean {
  if (item == null) return false;
  if (item.status == null || item.status === '') return true;
  return Number(item.status) === 1;
}

function isRootParentId(parentId: unknown): boolean {
  return parentId == null || parentId === '' || Number(parentId) === 0;
}

/** 扁平化菜单树，便于按 type/code 检索 */
function flattenMenuNodes(menus: MenuPermissionItem[]): MenuPermissionItem[] {
  const out: MenuPermissionItem[] = [];
  const walk = (nodes: MenuPermissionItem[]) => {
    nodes.forEach((node) => {
      out.push(node);
      if (
        Array.isArray((node as any).children) &&
        (node as any).children.length
      ) {
        walk((node as any).children);
      }
    });
  };
  walk(menus);
  return out;
}

function resolveMenuSource(menus?: MenuPermissionItem[]): MenuPermissionItem[] {
  if (menus?.length) {
    return flattenMenuNodes(menus);
  }
  return getAllMenusFromStorage();
}

function menuBelongsToSubsystem(
  menu: MenuPermissionItem,
  meta: (typeof SUBSYSTEM_META)[number],
): boolean {
  const code = String(menu.code || '').trim();
  const path = String(menu.path || '').trim();
  if (code === meta.code) {
    return true;
  }

  // 历史 member:space / member:system 归项目配置，不归会员
  const isLegacyProjectCode =
    code.startsWith('member:space') || code.startsWith('member:system');
  const isLegacyProjectPath =
    path.startsWith('/project/') ||
    path.startsWith('/member/system') ||
    path.startsWith('/member/space') ||
    path.startsWith('/system/space');

  if (meta.code === SUBSYSTEM_CODES.member) {
    if (isLegacyProjectCode || isLegacyProjectPath) {
      return false;
    }
  }
  if (meta.code === SUBSYSTEM_CODES.project) {
    if (isLegacyProjectCode || isLegacyProjectPath) {
      return true;
    }
  }

  if (meta.codePrefixes.some((p) => code === p || code.startsWith(p))) {
    return true;
  }
  if (
    path &&
    meta.pathPrefixes.some(
      (p) => path.startsWith(p) || path === p.replace(/\/$/, '') || path === p,
    )
  ) {
    return true;
  }
  return false;
}

function findFirstVisibleMenuPath(
  nodes: (MenuPermissionItem & { children?: MenuPermissionItem[] })[],
): string | undefined {
  const sorted = [...nodes].sort(
    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
  );
  for (const node of sorted) {
    if (!isPermissionItemEnabled(node)) {
      continue;
    }
    if (
      node.type === 'MENU' &&
      node.isHidden !== 1 &&
      node.path &&
      node.path.trim()
    ) {
      return node.path.trim();
    }
    if (node.children?.length) {
      const childPath = findFirstVisibleMenuPath(node.children);
      if (childPath) {
        return childPath;
      }
    }
  }
  return undefined;
}

/**
 * 从权限菜单（扁平或树）提取顶栏子系统入口。
 * 优先使用已授权的 type=SUBSYSTEM；若仅有其下 MODULE/MENU，则按命名空间推断（仍只展示有授权的子系统）。
 */
export function getSubsystemEntriesFromPermissions(
  menus?: MenuPermissionItem[],
): Array<{
  key: string;
  name: string;
  url: string;
  code: string;
  sortOrder: number;
}> {
  const source = resolveMenuSource(menus).filter(isPermissionItemEnabled);
  if (!source.length) {
    return [];
  }

  const explicit = source.filter(
    (m) =>
      (m.type === 'SUBSYSTEM' ||
        String(m.code || '').startsWith('subsystem:')) &&
      isRootParentId(m.parentId),
  );

  const byCode = new Map(explicit.map((m) => [m.code, m]));

  const entries: Array<{
    key: string;
    name: string;
    url: string;
    code: string;
    sortOrder: number;
  }> = [];

  for (const meta of SUBSYSTEM_META) {
    const node = byCode.get(meta.code);
    const hasGrant =
      !!node || source.some((m) => menuBelongsToSubsystem(m, meta));
    if (!hasGrant) {
      continue;
    }
    const url = (node?.path || '').trim() || meta.defaultUrl;
    entries.push({
      key: meta.code,
      name: node?.name || meta.name,
      url,
      code: meta.code,
      sortOrder: node?.sortOrder ?? meta.sortOrder,
    });
  }

  return entries.sort((a, b) => a.sortOrder - b.sortOrder);
}

/** 当前角色是否拥有指定子系统（顶栏/路由守卫共用） */
export function hasSubsystemPermission(
  subsystemCode: string,
  menus?: MenuPermissionItem[],
): boolean {
  if (!subsystemCode) return false;
  return getSubsystemEntriesFromPermissions(menus).some(
    (e) => e.code === subsystemCode,
  );
}

/** 取某子系统下第一个可见 MENU 路径 */
function findSubsystemFirstMenuPath(
  code: string,
  menus?: MenuPermissionItem[],
): string | undefined {
  const source = resolveMenuSource(menus);
  if (!source.length) {
    return undefined;
  }
  const tree = buildMenuTree(source);
  const root = tree.find((item) => item.code === code);
  if (root?.children?.length) {
    return findFirstVisibleMenuPath(root.children);
  }
  // 未按 SUBSYSTEM 组树时：从扁平归属里拼一层再找
  const meta = SUBSYSTEM_META.find((item) => item.code === code);
  if (!meta) {
    return undefined;
  }
  const orphans = source.filter(
    (m) => menuBelongsToSubsystem(m, meta) && m.type === 'MENU',
  );
  if (orphans.length) {
    return findFirstVisibleMenuPath(orphans);
  }
  return undefined;
}

/** 「项目配置」默认入口 */
export function getSystemSettingsDefaultPath(
  menus?: MenuPermissionItem[],
): string {
  return (
    findSubsystemFirstMenuPath(SUBSYSTEM_CODES.project, menus) ||
    POOL_SYSTEM_DEFAULT_PATH
  );
}

/** 按子系统解析默认入口（优先权限树第一个可见叶子，再回退元数据 defaultUrl） */
export function getSubsystemDefaultPath(
  code: string,
  menus?: MenuPermissionItem[],
): string {
  const fromTree = findSubsystemFirstMenuPath(code, menus);
  if (fromTree) {
    return fromTree;
  }
  if (code === SUBSYSTEM_CODES.project) {
    return POOL_SYSTEM_DEFAULT_PATH;
  }
  const meta = SUBSYSTEM_META.find((m) => m.code === code);
  return meta?.defaultUrl || '/member/home/overview';
}

/**
 * 登录成功 / 根路径：按角色已授权子系统顺序，落到第一个可访问菜单。
 * 无任何菜单权限时返回空字符串，由调用方决定去登录页或提示。
 */
export function resolveFirstAccessiblePath(
  menus?: MenuPermissionItem[],
): string {
  const entries = getSubsystemEntriesFromPermissions(menus);
  for (const entry of entries) {
    const path = getSubsystemDefaultPath(entry.code, menus);
    if (path?.trim()) {
      return path.trim();
    }
  }
  return '';
}
